import {
  AccountData,
  AudienceList,
  Event,
  PhoneInfo,
  SavedFormQuestion,
  SearchHistory,
  TicketV2,
  Follower,
  TeamMember,
  MembershipPlan,
  Membership,
  ThemeType,
  NotificationType,
  SavedFormQuestionState,
  ContactCardFirstTextState,
  DataFilter,
  OnboardingFlow,
  OnboardingFlowType,
  InstagramAccount,
  FacebookPage,
} from "@markit/common.types";
import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  Batch,
  getPhoneNumberRef,
  getSoftUserRef,
  getUserAudienceListsSnap,
  getUserData,
  getUserDataFilterRef,
  getUserDataFiltersSnap,
  getUserInstagramAccountsSnap,
  getUserFacebookPagesSnap,
  getUserOnboardingFlowRef,
  getUserOnboardingFlowsSnap,
  getUserRef,
  getUserSavedFormQuestionData,
  getUserSavedFormQuestionsSnap,
  getUserTeamMemberData,
  getUserTeamMemberRef,
  getUsernameRef,
} from "../../utils/FirebaseUtils";
import { AppState, AppThunk } from "../store";
import { USER_LOGGED_OUT } from "../actionTypes";
import {
  deleteDoc,
  firebaseAuth,
  firebaseStorage,
  getDoc,
  ref,
  setDoc,
  signOut,
  updateDoc,
} from "../../firebase";
import { deleteObject } from "firebase/storage";
import { API } from "../../API";
import { checkForPaymentEnabled } from "../../utils/stripeAccountUtils";
import { saveMediaToFirestore } from "../../utils/photoUtils";
import { createReferralFromSavedCode } from "../../utils/subscriptionUtils";
import {
  FAVORITES_LIST_NAME,
  hasSubscription,
  makeEmptyAccount,
} from "@markit/common.utils";
import { fetchNumAudienceListMembers } from "../../utils/audienceListUtils";
import { Mixpanel } from "../../context/AnalyticsService";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { removeAccountData } from "../localStorage";

export enum LoginState {
  // eslint-disable-next-line no-unused-vars
  LOGGED_IN,
  // eslint-disable-next-line no-unused-vars
  LOGGED_OUT,
  // eslint-disable-next-line no-unused-vars
  FETCHING,
}

export type AudienceListDetails = {
  list: AudienceList;
  numMembers: number;
};

export interface AccountState {
  loggedIn: LoginState;
  appInitialized: boolean;
  isRCA: boolean;
  redirectPath: string;
  linkTrackerId: string;
  accountData: AccountData;
  currentEventFormQuestions: SavedFormQuestion[];
  currentRoleTicket: TicketV2 | undefined;
  followingAccountData: AccountData[];
  followersData: Follower[];
  loadedAllFollowers: boolean;
  audienceListDetails: AudienceListDetails[];
  readyForPayment: boolean;
  savedQuestions: SavedFormQuestion[];
  teamMemberData: TeamMember | undefined;
  teamMemberAccountData: AccountData | undefined; // Account data of the team member account
  additionalAccountData: AccountData[];
  originalAccountAdmin: string; // helps keeps track of the original uid when are viewing a different account through admin mode
  dataFilters: DataFilter[];
  onboardingFlows: OnboardingFlow[];
  instagramAccounts: InstagramAccount[];
  facebookPages: FacebookPage[];
}

export const initialAccountState: AccountState = {
  loggedIn: LoginState.LOGGED_OUT,
  appInitialized: false,
  isRCA: false,
  redirectPath: "",
  linkTrackerId: "",
  accountData: makeEmptyAccount(),
  currentEventFormQuestions: [],
  currentRoleTicket: undefined,
  followingAccountData: [],
  followersData: [],
  loadedAllFollowers: false,
  audienceListDetails: [],
  readyForPayment: false,
  savedQuestions: [],
  teamMemberData: undefined,
  teamMemberAccountData: undefined,
  additionalAccountData: [],
  originalAccountAdmin: "",
  dataFilters: [],
  onboardingFlows: [],
  instagramAccounts: [],
  facebookPages: [],
};

export const accountSlice = createSlice({
  name: "account",
  initialState: initialAccountState,
  reducers: {
    login: (state, action: PayloadAction<AccountData>) => {
      state.loggedIn = LoginState.LOGGED_IN;
      state.accountData = action.payload;
    },
    logout: (state) => {
      state.loggedIn = LoginState.LOGGED_OUT;
      state.accountData = makeEmptyAccount();
      state.followingAccountData = [];
      state.followersData = [];
      state.loadedAllFollowers = false;
      state.audienceListDetails = [];
      state.redirectPath = "";
      state.savedQuestions = [];
      state.teamMemberData = undefined;
      state.teamMemberAccountData = undefined;
      state.additionalAccountData = [];
      state.originalAccountAdmin = "";
      state.dataFilters = [];
      state.onboardingFlows = [];
      state.instagramAccounts = [];
      state.facebookPages = [];
    },
    clearData: (state) => {
      state.followingAccountData = [];
      state.followersData = [];
      state.loadedAllFollowers = false;
      state.audienceListDetails = [];
      state.appInitialized = false;
      state.redirectPath = "";
      state.savedQuestions = [];
      state.teamMemberData = undefined;
      state.teamMemberAccountData = undefined;
      state.originalAccountAdmin = "";
      state.dataFilters = [];
      state.onboardingFlows = [];
      state.instagramAccounts = [];
      state.facebookPages = [];
    },
    setAppInitialized: (state, action: PayloadAction<boolean>) => {
      state.appInitialized = action.payload;
    },
    setOriginalAccountAdmin: (state, action: PayloadAction<string>) => {
      state.originalAccountAdmin = action.payload;
    },
    setLoggedIn: (state, action: PayloadAction<LoginState>) => {
      state.loggedIn = action.payload;
    },
    setIsRCA: (state, action: PayloadAction<boolean>) => {
      state.isRCA = action.payload;
    },
    setRedirectPath: (state, action: PayloadAction<string>) => {
      state.redirectPath = action.payload;
    },
    setLinkTrackerId: (state, action: PayloadAction<string>) => {
      state.linkTrackerId = action.payload;
    },
    updateAccount: (state, action: PayloadAction<Partial<AccountData>>) => {
      state.accountData = { ...state.accountData, ...action.payload };
    },
    updateCancelAtEnd: (state, action: PayloadAction<boolean>) => {
      state.accountData.customer.cancelAtEnd = action.payload;
    },
    updateSubscriptionState: (state, action: PayloadAction<any[]>) => {
      state.accountData.customer.state = action.payload[0];
      state.accountData.customer.frozenDate = action.payload[1];
    },
    updateSignedAgreement: (state, action: PayloadAction<boolean>) => {
      (state.teamMemberAccountData || state.accountData).signedAgreement =
        action.payload;
    },
    updateProfilePicURL: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).profilePicURL =
        action.payload;
    },
    updatedSharedProfileLink: (state, action: PayloadAction<boolean>) => {
      (state.teamMemberAccountData || state.accountData).sharedProfileLink =
        action.payload;
    },
    updatePhoneShowedAccountApproval: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.accountData.customer.phoneVerification.showedAccountApproval =
        action.payload;
    },
    updateFullName: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).fullName =
        action.payload;
    },
    updateUsername: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).username =
        action.payload;
    },
    updateBio: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).bio = action.payload;
    },
    updateInstagram: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).instagram =
        action.payload;
    },
    updateLinkedin: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).linkedin =
        action.payload;
    },
    updateTwitter: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).twitter =
        action.payload;
    },
    updateTiktok: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).tiktok =
        action.payload;
    },
    updateSpotify: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).spotify =
        action.payload;
    },
    updateYoutube: (state, action: PayloadAction<string>) => {
      (state.teamMemberAccountData || state.accountData).youtube =
        action.payload;
    },
    updateToggleReminderTexts: (state, action: PayloadAction<boolean>) => {
      (state.teamMemberAccountData || state.accountData).toggleReminderTexts =
        action.payload;
    },
    updateToggleContactCardFirstText: (
      state,
      action: PayloadAction<ContactCardFirstTextState>
    ) => {
      (
        state.teamMemberAccountData || state.accountData
      ).toggleContactCardFirstText = action.payload;
    },
    updateBypassQuietHours: (state, action: PayloadAction<boolean>) => {
      (state.teamMemberAccountData || state.accountData).bypassQuietHours =
        action.payload;
    },
    finishInitialStartupProcess: (state) => {
      (
        state.teamMemberAccountData || state.accountData
      ).haveNotFinishedInitialStartup = false;
    },
    setCurrentEventFormQuestions: (
      state,
      action: PayloadAction<SavedFormQuestion[]>
    ) => {
      state.currentEventFormQuestions = action.payload;
    },
    setCurrentRoleTicket: (
      state,
      action: PayloadAction<[TicketV2, Event] | undefined>
    ) => {
      state.currentRoleTicket = action.payload ? action.payload[0] : undefined;
    },
    updateSearchHistory: (state, action: PayloadAction<SearchHistory>) => {
      state.accountData.searchHistory = action.payload;
    },
    addMultipleFollowingAccountData: (
      state,
      action: PayloadAction<AccountData[]>
    ) => {
      state.followingAccountData = Array.from(
        [...state.followingAccountData, ...action.payload]
          .reduce((map, item) => {
            map.set(item.uid, item);
            return map;
          }, new Map())
          .values()
      );
    },
    addMultipleFollowerData: (state, action: PayloadAction<Follower[]>) => {
      const incomingById = new Map(
        action.payload.map((item) => [item.uid, item])
      );
      state.followersData = state.followersData.map((item) => {
        const updated = incomingById.get(item.uid);
        if (updated) {
          incomingById.delete(item.uid);
          return updated;
        }
        return item;
      });
      if (incomingById.size > 0) {
        state.followersData = state.followersData.concat(
          Array.from(incomingById.values())
        );
      }
    },
    initializeFollowerData: (state, action: PayloadAction<Follower[]>) => {
      state.followersData = action.payload;
    },
    setLoadedAllFollowers: (state, action: PayloadAction<boolean>) => {
      state.loadedAllFollowers = action.payload;
    },
    setReadyForPayment: (state, action: PayloadAction<boolean>) => {
      state.readyForPayment = action.payload;
    },
    addAudienceList: (state, action: PayloadAction<AudienceListDetails>) => {
      const { list } = action.payload;
      // Replace any row with the same list id (upsert).
      let next = state.audienceListDetails.filter(
        (detail) => detail.list.id !== list.id
      );
      // Favorites is a singleton by name: concurrent creates can return different ids.
      if (list.name === FAVORITES_LIST_NAME) {
        next = next.filter(
          (detail) => detail.list.name !== FAVORITES_LIST_NAME
        );
      }
      state.audienceListDetails = next.concat(action.payload);
    },
    addAudienceListNumMembers: (
      state,
      action: PayloadAction<{ listId: string; numMembers: number }>
    ) => {
      const { listId, numMembers } = action.payload;
      state.audienceListDetails = state.audienceListDetails.map((listDetail) =>
        listDetail.list.id === listId
          ? { ...listDetail, numMembers }
          : listDetail
      );
    },
    removeAudienceList: (state, action: PayloadAction<string>) => {
      state.audienceListDetails = state.audienceListDetails.filter(
        (audienceListDetails) => audienceListDetails.list.id !== action.payload
      );
    },
    initializeAudienceLists: (
      state,
      action: PayloadAction<AudienceListDetails[]>
    ) => {
      state.audienceListDetails = action.payload;
    },
    setSavedFormQuestions: (
      state,
      action: PayloadAction<SavedFormQuestion[]>
    ) => {
      state.savedQuestions = action.payload;
    },
    setOnboardingFlows: (state, action: PayloadAction<OnboardingFlow[]>) => {
      state.onboardingFlows = action.payload;
    },
    addSavedFormQuestions: (
      state,
      action: PayloadAction<SavedFormQuestion[]>
    ) => {
      for (let i = 0; i < action.payload.length; i++) {
        if (
          !state.savedQuestions.some(
            (question) => question.id === action.payload[i].id
          )
        ) {
          state.savedQuestions = state.savedQuestions.concat(action.payload[i]);
        } else {
          state.savedQuestions = state.savedQuestions.filter(
            (question) => question.id !== action.payload[i].id
          );
          state.savedQuestions = state.savedQuestions.concat(action.payload[i]);
        }
      }
    },
    removeSavedFormQuestions: (
      state,
      action: PayloadAction<SavedFormQuestion[]>
    ) => {
      state.savedQuestions = state.savedQuestions.filter(
        (question) => !action.payload.some((q) => q.id === question.id)
      );
    },
    updateCurrentEventFormQuestions: (
      state,
      action: PayloadAction<SavedFormQuestion[]>
    ) => {
      for (let i = 0; i < action.payload.length; i++) {
        const index = state.currentEventFormQuestions.findIndex(
          (question) => question.id === action.payload[i].id
        );
        if (index !== -1) {
          state.currentEventFormQuestions.splice(index, 1, action.payload[i]);
        } else {
          state.currentEventFormQuestions =
            state.currentEventFormQuestions.concat(action.payload);
        }
      }
    },
    reorderCurrentEventFormQuestions: (
      state,
      action: PayloadAction<SavedFormQuestion[]>
    ) => {
      state.currentEventFormQuestions = action.payload;
    },
    deleteCurrentEventFormQuestion: (state, action: PayloadAction<string>) => {
      state.currentEventFormQuestions = state.currentEventFormQuestions.filter(
        (question) => question.id !== action.payload
      );
    },
    resetCurrentEventFormQuestions: (state) => {
      state.currentEventFormQuestions = [];
    },
    updateProfileQuestions: (state, action: PayloadAction<string[]>) => {
      state.accountData.formQuestions = action.payload;
    },
    removeProfileQuestion: (state, action: PayloadAction<string>) => {
      const index = state.savedQuestions.findIndex(
        (question) => question.id === action.payload
      );
      state.savedQuestions[index].onProfileFollow = false;
      state.accountData.formQuestions = state.accountData.formQuestions.filter(
        (questionId) => questionId !== action.payload
      );
    },
    setTeamMemberData: (state, action: PayloadAction<TeamMember>) => {
      state.teamMemberData = action.payload;
    },
    setTeamMemberAccountData: (state, action: PayloadAction<AccountData>) => {
      state.teamMemberAccountData = action.payload;
    },
    addAccount: (
      state,
      action: PayloadAction<{
        prevAccountData: AccountData;
        newAccountData: AccountData;
      }>
    ) => {
      const { prevAccountData, newAccountData } = action.payload;

      // Move current accountData to additionalAccountData if it exists
      const currentAccountExists = state.additionalAccountData.find(
        (account) => account.uid === prevAccountData.uid
      );

      if (!currentAccountExists) {
        state.additionalAccountData.push({ ...prevAccountData });
      }

      // Set the new account as the main accountData
      state.accountData = newAccountData;

      // Remove the new account from additionalAccountData if it exists there
      state.additionalAccountData = state.additionalAccountData.filter(
        (account) => account.uid !== newAccountData.uid
      );
    },
    removeAccount: (state, action: PayloadAction<string>) => {
      const accountIdToRemove = action.payload;
      state.additionalAccountData = state.additionalAccountData.filter(
        (account) => account.uid !== accountIdToRemove
      );
    },
    switchToAccount: (state, action: PayloadAction<string>) => {
      const targetAccountId = action.payload;

      // Find the target account in additional accounts
      const targetAccount = state.additionalAccountData.find(
        (account) => account.uid === targetAccountId
      );

      if (targetAccount) {
        // Move current account to additional accounts if it's not already there
        const currentAccountExists = state.additionalAccountData.find(
          (account) => account.uid === state.accountData.uid
        );

        if (!currentAccountExists) {
          state.additionalAccountData.push({ ...state.accountData });
        }

        // Switch to target account
        state.accountData = targetAccount;

        // Remove target account from additional accounts
        state.additionalAccountData = state.additionalAccountData.filter(
          (account) => account.uid !== targetAccountId
        );
      }
    },
    setAdditionalAccounts: (state, action: PayloadAction<AccountData[]>) => {
      state.additionalAccountData = action.payload;
    },
    setDataFilters: (state, action: PayloadAction<DataFilter[]>) => {
      state.dataFilters = action.payload;
    },
    setInstagramAccounts: (
      state,
      action: PayloadAction<InstagramAccount[]>
    ) => {
      state.instagramAccounts = action.payload;
    },
    updateInstagramAccount: (
      state,
      action: PayloadAction<InstagramAccount>
    ) => {
      state.instagramAccounts = state.instagramAccounts.map((account) =>
        account.id === action.payload.id ? action.payload : account
      );
    },
    setFacebookPages: (state, action: PayloadAction<FacebookPage[]>) => {
      state.facebookPages = action.payload;
    },
    updateFacebookPage: (state, action: PayloadAction<FacebookPage>) => {
      state.facebookPages = state.facebookPages.map((page) =>
        page.id === action.payload.id ? action.payload : page
      );
    },
    manageDataFilter: (
      state,
      action: PayloadAction<{
        operation: "add" | "update" | "remove";
        filter: DataFilter;
      }>
    ) => {
      const payload = action.payload;

      if (payload.operation === "remove") {
        state.dataFilters = state.dataFilters.filter(
          (filter) => filter.id !== payload.filter.id
        );
        return;
      }

      const filterToSave = payload.filter;
      const existingFilterIndex = state.dataFilters.findIndex(
        (filter) => filter.id === filterToSave.id
      );

      if (existingFilterIndex >= 0) {
        state.dataFilters[existingFilterIndex] = filterToSave;
      } else {
        state.dataFilters.push(filterToSave);
      }
    },
  },
});

export const accountActions = accountSlice.actions;
export const accountReducer = accountSlice.reducer;

export const logoutAllUsers =
  (disableBanner?: boolean): AppThunk =>
  async (dispatch, getState) => {
    const { additionalAccountData } = getState().account;
    // Need to change to logged out state before calling signOut to prevent redirect on LandingPageWrapper
    dispatch(accountActions.setLoggedIn(LoginState.LOGGED_OUT));
    await signOut(firebaseAuth);
    dispatch({ type: USER_LOGGED_OUT });

    if (!disableBanner) {
      showNotificationBanner(
        dispatch,
        additionalAccountData.length > 0
          ? "Signed out of all accounts"
          : "Signed out of account",
        NotificationType.NEGATIVE,
        "ion:exit"
      );
    }

    // Add a short delay to ensure firebase auth sign out has completed. This makes sure the redux store gets cleared on the other tabs.
    setTimeout(() => {
      localStorage.clear();
      // Notify other tabs about logout
      localStorage.setItem("logout", Date.now().toString());
    }, 1000);
  };

export const logoutSingleUser =
  (uid: string, disableBanner?: boolean): AppThunk =>
  async (dispatch) => {
    // Signing out of an additional account
    dispatch(accountActions.removeAccount(uid));
    removeAccountData(uid);

    if (!disableBanner) {
      showNotificationBanner(
        dispatch,
        "Signed out of account",
        NotificationType.NEGATIVE,
        "ion:exit"
      );
    }
  };

export const updateAccountData =
  (newAccountData: AccountData): AppThunk =>
  async (dispatch, getState) => {
    const { accountData, teamMemberAccountData } = getState().account;
    const userDataToShow = teamMemberAccountData || accountData;
    const userRef = getUserRef(newAccountData.uid);
    await setDoc(userRef, newAccountData);
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        let contactCardInfoChanged = false;
        const usernameLowercase = newAccountData.username.toLowerCase();
        if (userDataToShow.username !== usernameLowercase) {
          contactCardInfoChanged = true;
          await dispatch(
            changeUsername(
              newAccountData.uid,
              accountData.username,
              usernameLowercase
            )
          );
        }
        if (teamMemberAccountData) {
          // Update team member account data if on team member account
          const teamMemberRef = getUserTeamMemberRef(
            accountData.uid,
            teamMemberAccountData.uid
          );
          updateDoc(teamMemberRef, { fullName: newAccountData.fullName });
          dispatch(accountActions.setTeamMemberAccountData(newAccountData));
        } else {
          dispatch(accountActions.updateAccount(newAccountData));
        }
        if (userDataToShow.profilePicURL !== newAccountData.profilePicURL) {
          await dispatch(
            changeProfilePicURL(newAccountData.profilePicURL, newAccountData)
          );
        } else if (userDataToShow.fullName !== newAccountData.fullName) {
          contactCardInfoChanged = true;
        }

        if (contactCardInfoChanged && hasSubscription(newAccountData)) {
          // regenerate contact card if subscription user and if full name or username changed but not profile pic (since profile pic function will update contact card if that changed)
          // uses accountData.customer.phone instead of isApprovedPhoneStatus bc we generate original contact card before phone is approved
          await API.user
            .regenerateContactCard({ uid: newAccountData.uid })
            .catch((e) => {
              console.log(e.message);
            });
        }
      }
    } catch (error: any) {
      alert("Error updating account data: " + error.message);
    }
  };

export const changeSignedAgreement =
  (signedAgreement: boolean, uid: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, { signedAgreement: signedAgreement });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateSignedAgreement(signedAgreement));
      }
    } catch (error: any) {
      alert("Error updating signed agreement: " + error.message);
    }
  };

export const changeProfilePicURL =
  (
    blobUrl: string,
    userData: AccountData,
    oldProfilePicURL?: string
  ): AppThunk =>
  async (dispatch) => {
    const url = await saveMediaToFirestore(
      blobUrl,
      userData.uid +
        "/profilePicture/" +
        userData.uid +
        "+" +
        new Date().toISOString(),
      userData.customer.contactCardUrl
    );

    try {
      const userRef = getUserRef(userData.uid);
      await updateDoc(userRef, { profilePicURL: url });
      dispatch(accountActions.updateProfilePicURL(url));

      // Delete the old profile picture if it exists
      if (oldProfilePicURL) {
        const oldFileRef = ref(firebaseStorage, oldProfilePicURL);
        await deleteObject(oldFileRef);
      }

      // regenerate contact card if subscription user
      // uses accountData.customer.phone instead of isApprovedPhoneStatus bc we generate original contact card before phone is approved
      if (userData.customer.phone !== "") {
        // Wait for a short delay to ensure Firebase has propagated the update
        await new Promise((resolve) => setTimeout(resolve, 1000));

        await API.user
          .regenerateContactCard({
            uid: userData.uid,
          })
          .catch((e) => {
            console.log(e.message);
          });
      }
    } catch (err: any) {
      alert("Error updating profile pic URL: " + err.message);
    }
  };

/**
 * Updates if the user has shared their profile link
 */
export const changeSharedProfileLink =
  (sharedProfileLink: boolean, uid: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, { sharedProfileLink });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updatedSharedProfileLink(sharedProfileLink));
      }
    } catch (error: any) {
      alert("Error updating shared profile link: " + error.message);
    }
  };

/**
 * Updates if the user has seen the phone verification account approval
 */
export const changePhoneShowedAccountApproval =
  (showedAccountApproval: boolean, uid: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    await updateDoc(userRef, {
      "customer.phoneVerification.showedAccountApproval": true,
    } as Partial<Record<string, any>>);
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(
          accountActions.updatePhoneShowedAccountApproval(showedAccountApproval)
        );
      }
    } catch (error: any) {
      alert("Error updating shared profile link: " + error.message);
    }
  };

/**
 * Updates the user's fullname
 */
export const changeFullName =
  (uid: string, fullName: string): AppThunk =>
  async (dispatch, getState) => {
    const { teamMemberAccountData } = getState().account;
    const batch = new Batch("Error updating fullname");
    try {
      const userRef = getUserRef(uid);
      batch.update(userRef, { fullName: fullName });
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateFullName(fullName));
        if (teamMemberAccountData) {
          // Update teamMember data to keep matching between fullName
          const teamMemberRef = getUserTeamMemberRef(
            teamMemberAccountData.teamOwnerUid,
            uid
          );
          batch.update(teamMemberRef, { fullName: fullName });
        }
      }
      await batch.commit();
    } catch (e: any) {
      alert("Error updating fullname: " + e.message);
    }
  };

/**
 * Updates the user's username
 */
export const changeUsername =
  (uid: string, previousUsername: string, username: string): AppThunk =>
  async (dispatch) => {
    const batch = new Batch("Error updating username");
    const previousUsernameRef = getUsernameRef(previousUsername);
    const usernameRef = getUsernameRef(username);
    const accountRef = getUserRef(uid);

    batch.delete(previousUsernameRef);
    batch.set(usernameRef, {
      username: username,
    });
    batch.update(accountRef, { username: username });

    try {
      await batch.commit();
      dispatch(accountActions.updateUsername(username));
    } catch (e: any) {
      alert("Error updating username");
      console.error("Error updating username: " + e.message);
    }
  };

/* Updates the user's bio field */
export const changeBio =
  (uid: string, bio: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, {
      bio: bio,
    });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateBio(bio));
      }
    } catch (e: any) {
      alert("Error updating bio: " + e.message);
    }
  };

/* Updates the user's instagram field */
export const changeInstagram =
  (uid: string, instagram: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, {
      instagram: instagram,
    });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateInstagram(instagram));
      }
    } catch (e: any) {
      alert("Error updating instagram: " + e.message);
    }
  };

/* Updates the user's linkedin field */
export const changeLinkedin =
  (uid: string, linkedin: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, {
      linkedin: linkedin,
    });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateLinkedin(linkedin));
      }
    } catch (e: any) {
      alert("Error updating linkedin: " + e.message);
    }
  };

/* Updates the user's twitter field */
export const changeTwitter =
  (uid: string, twitter: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, {
      twitter: twitter,
    });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateTwitter(twitter));
      }
    } catch (e: any) {
      alert("Error updating twitter: " + e.message);
    }
  };

/* Updates the user's tiktok field */
export const changeTiktok =
  (uid: string, tiktok: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, {
      tiktok: tiktok,
    });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateTiktok(tiktok));
      }
    } catch (e: any) {
      alert("Error updating tiktok: " + e.message);
    }
  };

/* Updates the user's spotify field */
export const changeSpotify =
  (uid: string, spotify: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, {
      spotify: spotify,
    });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateSpotify(spotify));
      }
    } catch (e: any) {
      alert("Error updating spotify: " + e.message);
    }
  };

/* Updates the user's youtube field */
export const changeYoutube =
  (uid: string, youtube: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, {
      youtube: youtube,
    });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateYoutube(youtube));
      }
    } catch (e: any) {
      alert("Error updating youtube: " + e.message);
    }
  };

export const changeToggleReminderTexts =
  (uid: string, toggleReminderTexts: boolean): AppThunk =>
  async (dispatch) => {
    try {
      const userRef = getUserRef(uid);
      updateDoc(userRef, { toggleReminderTexts: toggleReminderTexts });
      dispatch(accountActions.updateToggleReminderTexts(toggleReminderTexts));
    } catch (e: any) {
      alert("Error updating toggleReminderTexts: " + e.message);
    }
  };

export const changeToggleContactCardFirstText =
  (
    uid: string,
    toggleContactCardFirstText: ContactCardFirstTextState
  ): AppThunk =>
  async (dispatch) => {
    try {
      const userRef = getUserRef(uid);
      updateDoc(userRef, {
        toggleContactCardFirstText: toggleContactCardFirstText,
      });
      dispatch(
        accountActions.updateToggleContactCardFirstText(
          toggleContactCardFirstText
        )
      );
    } catch (e: any) {
      alert("Error updating toggleContactCardFirstText: " + e.message);
    }
  };

export const changeBypassQuietHours =
  (uid: string, bypassQuietHours: boolean, mixpanel: Mixpanel): AppThunk =>
  async (dispatch) => {
    try {
      const userRef = getUserRef(uid);
      updateDoc(userRef, { bypassQuietHours: bypassQuietHours });
      dispatch(accountActions.updateBypassQuietHours(bypassQuietHours));

      mixpanel.track("Toggled Bypass Quiet Hours", {
        distinct_id: uid,
        bypassQuietHours,
        from: "webapp",
      });
    } catch (e: any) {
      alert("Error updating bypassQuietHours: " + e.message);
    }
  };

/* Updates the profilePasswordProtect section on profile page */
export const changeProfilePasswordProtect =
  (uid: string, profilePassword: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, { profilePasswordProtect: profilePassword });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(
          accountActions.updateAccount({
            profilePasswordProtect: profilePassword,
          })
        );
      }
    } catch (e: any) {
      alert("Error updating changeProfilePasswordProtect: " + e.message);
    }
  };

export const changePhoneNumber =
  (phoneNumber: string, uid: string, oldPhoneNumber?: string): AppThunk =>
  async (dispatch) => {
    const batch = new Batch("Error updating phone number");
    if (oldPhoneNumber) {
      const oldPhoneNumberRef = getPhoneNumberRef(oldPhoneNumber);
      batch.delete(oldPhoneNumberRef);

      const softUserRef = getSoftUserRef(oldPhoneNumber);
      batch.delete(softUserRef);
    }

    if (phoneNumber !== "") {
      const phoneNumberRef = getPhoneNumberRef(phoneNumber);

      const phoneInfo: PhoneInfo = {
        lastFreeCreator: "",
        phoneNumber: phoneNumber,
        userId: uid,
        sentCompliance: false,
        sentComplianceFreeCreatorPhone: false,
        optOut: false,
        optOutFreeCreatorPhone: false,
        optOutTimestamp: "",
      };
      batch.set(phoneNumberRef, phoneInfo, { merge: false });
    }

    const userRef = getUserRef(uid);
    batch.update(userRef, {
      phoneNumber: phoneNumber,
    });

    try {
      await batch.commit();
      dispatch(accountActions.updateAccount({ phoneNumber: phoneNumber }));
    } catch (e: any) {
      alert("Error editing phone number.");
      console.error("Error editing phone number: " + e.message);
    }
  };

export const changeEmail =
  (email: string, uid: string): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(uid);
    updateDoc(userRef, { email: email });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateAccount({ email: email }));
      }
    } catch (error: any) {
      alert("Error updating email: " + error.message);
    }
  };

// This is called when a creator finishes the onboarding flow, either by choosing the free plan and finishing set up, or getting a subscription during onboarding
// If this this not called, the user has not finished setting up their account on the webapp sign up flow, and will be directed to this each time they log in until changed
export const finishInitialStartup =
  (
    accountSettings: AccountData,
    mixpanel: Mixpanel,
    signUpPhoneNumber?: string
  ): AppThunk =>
  async (dispatch) => {
    const userRef = getUserRef(accountSettings.uid);
    updateDoc(userRef, { haveNotFinishedInitialStartup: false });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        mixpanel.track("Finished Initial User Sign Up", {
          distinct_id: accountSettings.uid,
          fullName: accountSettings.fullName,
          username: accountSettings.username.toLowerCase(),
          email: accountSettings.email,
          phoneNumber: accountSettings.phoneNumber,
          inCreatorMode: accountSettings.inCreatorMode,
          instagram: accountSettings.instagram,
          linkedin: accountSettings.linkedin,
          twitter: accountSettings.twitter,
          tiktok: accountSettings.tiktok,
          spotify: accountSettings.spotify,
          youtube: accountSettings.youtube,
          source: "webapp",
        });
        dispatch(accountActions.finishInitialStartupProcess());

        // Only for creator mode accounts
        if (accountSettings.inCreatorMode) {
          // Create favorites list (except for team member accounts)
          if (!accountSettings.teamOwnerUid) {
            dispatch(createInitialFavoritesList(accountSettings));
          }

          // Record the referral from a shared link, even if they choose the free plan
          await createReferralFromSavedCode(accountSettings).catch(
            (e: any) => {
              console.error("Unable to record referral: " + e.message);
            }
          );

          // trigger Slack bot notification if creator is signing up
          await API.monitor
            .creatorSignUp({
              uid: accountSettings.uid,
              phoneNumber: signUpPhoneNumber || "",
            })
            .catch((e: any) => {
              console.error("Unable to trigger Slack bot for creator sign up");
            });
        }
      }
    } catch (e: any) {
      alert("Error finishing initial startup: " + e.message);
    }
  };

export const toggleInCreatorMode =
  (uid: string, inCreatorMode: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(
      accountActions.updateAccount({
        inCreatorMode: !inCreatorMode,
      })
    );

    const userRef = getUserRef(uid);
    const batch = new Batch("switching creator mode");
    await batch.update(userRef, {
      inCreatorMode: !inCreatorMode,
    });
    try {
      await batch.commit();
    } catch (err: any) {
      console.log("Error switching creator mode: " + err.message);
    }
  };

export const setShowedCategoriesEducationModal =
  (uid: string, showedCategoriesModal: boolean): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const educationModals = state.account.accountData.showedEducationModals;
    dispatch(
      accountActions.updateAccount({
        showedEducationModals: {
          ...educationModals,
          textCategoriesModal: showedCategoriesModal,
        },
      })
    );

    const userRef = getUserRef(uid);
    const batch = new Batch("switching showedEducationModal");
    await batch.update(userRef, {
      showedEducationModals: {
        ...educationModals,
        textCategoriesModal: showedCategoriesModal,
      },
    });
    await batch.commit();
  };

export const setShowImportantMessage =
  (uid: string): AppThunk =>
  async (dispatch) => {
    dispatch(
      accountActions.updateAccount({
        showImportantMessage: false,
      })
    );

    const userRef = getUserRef(uid);
    const batch = new Batch("Error updating important message modal state");
    await batch.update(userRef, { showImportantMessage: false });
    await batch.commit();
  };

/* Delete recently searched event(s) */
export const deleteSearchHistory =
  (
    uid: string,
    type: "marks" | "users" | "places",
    searchId: string
  ): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const history = state.account.accountData.searchHistory;
    const recent = history[type].filter((s) => s !== searchId);
    const updated = { ...history, [type]: recent };

    const userRef = getUserRef(uid);
    updateDoc(userRef, { searchHistory: updated });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateSearchHistory(updated));
      }
    } catch (error: any) {
      alert("Error deleting search history: " + error.message);
    }
  };

/* Add event to search history */
export const addSearchHistory =
  (
    uid: string,
    type: "marks" | "users" | "places",
    searchId: string
  ): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const history = state.account.accountData.searchHistory;
    const recent = [
      searchId,
      ...history[type].filter((s) => s !== searchId),
    ].slice(0, 5);
    const updated = { ...history, [type]: recent };
    const userRef = getUserRef(uid);
    updateDoc(userRef, { searchHistory: updated });
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        dispatch(accountActions.updateSearchHistory(updated));
      }
    } catch (error: any) {
      alert("Error updating search history: " + error.message);
    }
  };

export const fetchReadyForPayment =
  (uid: string): AppThunk =>
  async (dispatch) => {
    const userData = await getUserData(uid);
    if (userData) {
      const readyForPayment = await checkForPaymentEnabled(userData);
      dispatch(accountActions.setReadyForPayment(readyForPayment));
    } else {
      console.error("Could not fetch user data for: ", uid);
      dispatch(accountActions.setReadyForPayment(false));
    }
  };

// fetch all of a user's follower data
export const fetchAllUserFollowersData =
  (uid: string, reload?: boolean): AppThunk =>
  async (dispatch, getState) => {
    const { loadedAllFollowers } = getState().account;
    if (!loadedAllFollowers || reload) {
      const followersResponse = await API.user.followerUsersData({ uid });
      if (followersResponse) {
        const followersData = followersResponse.followerData;
        dispatch(accountActions.initializeFollowerData(followersData));
        dispatch(accountActions.setLoadedAllFollowers(true));
      }
    }
  };

export const fetchSavedFormQuestions =
  (uid: string): AppThunk =>
  async (dispatch) => {
    const savedFormQuestionsSnap = await getUserSavedFormQuestionsSnap(uid);

    const savedFormQuestions = savedFormQuestionsSnap.docs.map((doc) =>
      doc.data()
    );
    dispatch(accountActions.setSavedFormQuestions(savedFormQuestions));
  };

// Derives the incomplete-only subset of onboardingFlows for quick access.
export const getIncompleteOnboardingFlows = createSelector(
  (state: AppState) => state.account.onboardingFlows,
  (onboardingFlows) =>
    onboardingFlows.filter((flow) => !flow.platforms.web.completed)
);

// Fetches every onboarding flow doc (completed + incomplete). A flow type
// with no doc has never been created (used for the "Activate" state).
export const fetchOnboardingFlows =
  (uid: string): AppThunk =>
  async (dispatch) => {
    const onboardingFlowsSnap = await getUserOnboardingFlowsSnap(uid);
    const onboardingFlows = onboardingFlowsSnap.docs.map((doc) => doc.data());
    dispatch(accountActions.setOnboardingFlows(onboardingFlows));
  };

// Marks an onboarding flow completed or not
export const toggleOnboardingFlow =
  (
    uid: string,
    type: OnboardingFlowType,
    completed: boolean,
    skipped: boolean
  ): AppThunk =>
  async (dispatch, getState) => {
    const { onboardingFlows, accountData } = getState().account;
    const flowRef = getUserOnboardingFlowRef(uid, type);
    await updateDoc(flowRef, { "platforms.web.completed": completed });
    dispatch(
      accountActions.setOnboardingFlows(
        onboardingFlows.map((flow) =>
          flow.id === type
            ? { ...flow, platforms: { ...flow.platforms, web: { completed } } }
            : flow
        )
      )
    );

    if (completed) {
      API.monitor.triggerSlackAlert({
        channel: "#account-setup-updates",
        title: `Onboarding Tour ${skipped ? "Skipped" : "Completed"}`,
        message: `User: ${
          accountData.username
        } ${skipped ? "skipped" : "completed"} the "${type}" onboarding tour on web`,
      });
    }
  };

// Creates a web onboarding flow doc for a flow type that has never had one
// (used for the Tutorial Manager's "Activate" state)
export const activateOnboardingFlow =
  (uid: string, type: OnboardingFlowType): AppThunk =>
  async (dispatch, getState) => {
    const { onboardingFlows } = getState().account;
    const flowRef = getUserOnboardingFlowRef(uid, type);
    const newFlow: OnboardingFlow = {
      id: type,
      createdAt: new Date().toISOString(),
      platforms: { web: { completed: false }, app: { completed: false } },
    };
    await setDoc(flowRef, newFlow);
    dispatch(accountActions.setOnboardingFlows([...onboardingFlows, newFlow]));
  };

export const createSingleSavedQuestion =
  (uid: string, question: SavedFormQuestion): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.createSavedFormQuestion({
        uid: uid,
        savedFormQuestion: question,
      });
      dispatch(accountActions.addSavedFormQuestions([question]));
    } catch (e: any) {
      console.error(e.message);
    }
  };

export const updateSavedQuestion =
  (uid: string, question: SavedFormQuestion): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.updateSavedFormQuestion({
        uid: uid,
        savedFormQuestion: question,
      });
      dispatch(accountActions.addSavedFormQuestions([question]));
    } catch (e: any) {
      console.error(e.message);
    }
  };

export const deleteSavedQuestion =
  (userId: string, questionId: string, numResponses: number): AppThunk =>
  async (dispatch) => {
    const formQuestionData = await getUserSavedFormQuestionData(
      userId,
      questionId
    );
    if (formQuestionData) {
      await API.profile.deleteSavedFormQuestion({
        uid: userId,
        formQuestionId: questionId,
        numResponses: numResponses,
      });
      const numTimesUsed = formQuestionData.onProfileFollow
        ? formQuestionData.eventIds.length + 1
        : formQuestionData.eventIds.length;

      if (numResponses > 0 || numTimesUsed > 0) {
        const updatedFormQuestionData: SavedFormQuestion = {
          ...formQuestionData,
          state: SavedFormQuestionState.INACTIVE,
        };
        dispatch(
          accountActions.addSavedFormQuestions([updatedFormQuestionData])
        );
      } else {
        dispatch(accountActions.removeSavedFormQuestions([formQuestionData]));
      }
      showNotificationBanner(
        dispatch,
        numResponses > 0 || numTimesUsed > 0
          ? "Question Inactive"
          : "Question Deleted",
        NotificationType.NEGATIVE
      );
    }
  };

// Profile form questions
export const editSingleProfileQuestion =
  (uid: string, updatedFormQuestion: SavedFormQuestion): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.editSingleProfileQuestion({
        uid: uid,
        updatedFormQuestion: updatedFormQuestion,
      });
      dispatch(accountActions.addSavedFormQuestions([updatedFormQuestion]));
    } catch (e: any) {
      alert("Error editing single profile question");
      console.error("Error editing single profile question: " + e.message);
    }
  };

export const removeSingleProfileQuestion =
  (uid: string, formQuestionId: string): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.removeSingleProfileQuestion({
        uid: uid,
        formQuestionId: formQuestionId,
      });
      dispatch(accountActions.removeProfileQuestion(formQuestionId));
    } catch (e: any) {
      alert("Error removing single profile question");
      console.error("Error removing single profile question: " + e.message);
    }
  };

export const reorderProfileQuestions =
  (uid: string, savedFormQuestionIds: string[]): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.reorderProfileQuestions({
        uid: uid,
        savedFormQuestionIds: savedFormQuestionIds,
      });
      dispatch(accountActions.updateProfileQuestions(savedFormQuestionIds));
    } catch (e: any) {
      alert("Error reordering profile questions");
      console.error("Error reordering profile questions: " + e.message);
    }
  };

export const reorderMembershipPlans =
  (uid: string, membershipPlans: MembershipPlan[]): AppThunk =>
  async (dispatch, getState) => {
    const state = getState();
    const membership = state.account.accountData.membership;
    try {
      const newMembership: Membership = {
        ...membership,
        membershipPlans: membershipPlans,
      };
      const userRef = getUserRef(uid);
      await updateDoc(userRef, { membership: newMembership as any });
      dispatch(accountActions.updateAccount({ membership: newMembership }));
    } catch (e: any) {
      console.error("Error reordering membership plans: " + e.message);
    }
  };

export const updateMembershipTheme =
  (uid: string, newTheme: ThemeType): AppThunk =>
  async (dispatch, getState) => {
    const state = getState().account;
    const membership = state.accountData.membership;
    try {
      const newMembership: Membership = {
        ...membership,
        theme: newTheme,
      };
      const userRef = getUserRef(uid);
      await updateDoc(userRef, { membership: newMembership as any });
      dispatch(accountActions.updateAccount({ membership: newMembership }));
      showNotificationBanner(
        dispatch,
        "Updated!",
        NotificationType.AFFIRMATIVE
      );
    } catch (e: any) {
      console.error("Error updating membership theme: " + e.message);
    }
  };

export const updateProfileQuestions =
  (uid: string, savedFormQuestions: SavedFormQuestion[]): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.updateProfileQuestions({
        uid: uid,
        savedFormQuestions: savedFormQuestions,
      });
      dispatch(accountActions.addSavedFormQuestions(savedFormQuestions));
      dispatch(
        accountActions.updateProfileQuestions(
          savedFormQuestions.map((question) => question.id)
        )
      );
    } catch (e: any) {
      alert("Error updating profile questions");
      console.error("Error updating profile questions: " + e.message);
    }
  };

export const fetchAudienceLists =
  (userId: string): AppThunk =>
  async (dispatch) => {
    const audienceListsSnap = await getUserAudienceListsSnap(userId);
    const audienceLists: AudienceListDetails[] = await Promise.all(
      audienceListsSnap.docs.map(async (doc) => {
        const list = doc.data();
        const numMembers = await fetchNumAudienceListMembers(userId, list.id);
        return { list, numMembers };
      })
    );
    dispatch(accountActions.initializeAudienceLists(audienceLists));
  };

export const createAudienceList =
  (uid: string, list: AudienceList, members?: string[]): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.createAudienceList({
        uid,
        list,
        members,
      });
      const listDetail: AudienceListDetails = {
        list,
        numMembers: members ? members.length : 0,
      };
      dispatch(accountActions.addAudienceList(listDetail));
    } catch (e: any) {
      alert("Error adding user's audience list: " + e.message);
    }
  };

export const updateAudienceList =
  (uid: string, updatedAudienceList: AudienceList): AppThunk =>
  async (dispatch, getState) => {
    const audienceLists = getState().account.audienceListDetails;
    try {
      const foundListDetail = audienceLists.find(
        (listDetail) => listDetail.list.id === updatedAudienceList.id
      );
      const { audienceList } = await API.profile.updateAudienceList({
        uid: uid,
        list: updatedAudienceList,
      });
      if (foundListDetail) {
        const newListDetail: AudienceListDetails = {
          list: audienceList,
          numMembers: foundListDetail.numMembers,
        };
        dispatch(accountActions.addAudienceList(newListDetail));
      }
    } catch (e: any) {
      alert("Error updating user's audience list: " + e.message);
    }
  };

export const deleteAudienceList =
  (uid: string, audienceListId: string): AppThunk =>
  async (dispatch) => {
    try {
      await API.profile.deleteAudienceList({
        uid: uid,
        listId: audienceListId,
      });
      dispatch(accountActions.removeAudienceList(audienceListId));
    } catch (e: any) {
      alert("Error deleting user's audience list: " + e.message);
    }
  };

// Adds/Removes specified members to a specific audience list
export const addMultipleAudienceListMembers =
  (
    uid: string,
    list: AudienceList,
    newMembers: string[],
    isAddOnly?: boolean
  ): AppThunk =>
  async (dispatch) => {
    try {
      const { audienceList } = await API.profile.addMultipleAudienceListMembers(
        {
          uid,
          list,
          newMembers,
          isAddOnly,
        }
      );
      let numMembers = newMembers.length;
      // (TODO: change how we update the numMembers in redux after list architecture change)
      if (isAddOnly) {
        // If only adding new members, add to current count
        numMembers = await fetchNumAudienceListMembers(uid, list.id);
      }
      const listDetail: AudienceListDetails = {
        list: audienceList,
        numMembers,
      };
      dispatch(accountActions.addAudienceList(listDetail));
    } catch (e: any) {
      alert("Error adding users to Audience List");
      console.error("Error adding users to Audience List", e.message);
    }
  };

// Removes a single specified audience list member from list
export const removeSingleAudienceListMember =
  (uid: string, list: AudienceList, removedMember: string): AppThunk =>
  async (dispatch, getState) => {
    const audienceLists = getState().account.audienceListDetails;
    try {
      const { audienceList } = await API.profile.removeSingleAudienceListMember(
        {
          uid,
          list,
          newMemberUid: removedMember,
        }
      );
      const foundListDetail = audienceLists.find(
        (listDetail) => listDetail.list.id === list.id
      );
      if (foundListDetail) {
        const listDetail: AudienceListDetails = {
          list: audienceList,
          numMembers: foundListDetail.numMembers - 1,
        };
        dispatch(accountActions.addAudienceList(listDetail));
      }
    } catch (e: any) {
      alert("Error removing user to Audience List");
      console.error("Error removing user to Audience List", e.message);
    }
  };

// Create Favorites list if not created yet (checked on initial app load)
export const createInitialFavoritesList =
  (userData: AccountData): AppThunk =>
  async (dispatch) => {
    const { audienceList } = await API.profile.createFavoritesList({
      userData,
    });

    const listDetail: AudienceListDetails = {
      list: audienceList,
      numMembers: 0,
    };
    dispatch(accountActions.addAudienceList(listDetail));
  };

// Loads the relevant team member data for when a team member is logged in
export const loadTeamMemberData =
  (userData: AccountData): AppThunk =>
  async (dispatch) => {
    dispatch(accountActions.setTeamMemberAccountData(userData));
    const teamMemberData = await getUserTeamMemberData(
      userData.teamOwnerUid,
      userData.uid
    );
    if (teamMemberData) {
      dispatch(accountActions.setTeamMemberData(teamMemberData));
    }
  };

// Loads the initial data filters for the user (via team member or owner)
export const loadInitialDataFilters =
  (userId: string): AppThunk =>
  async (dispatch, getState) => {
    const { teamMemberAccountData } = getState().account;
    const dataFilterUserId = teamMemberAccountData?.uid || userId;
    const dataFiltersSnap = await getUserDataFiltersSnap(dataFilterUserId);
    const dataFilters = dataFiltersSnap.docs.map((doc) => doc.data());
    dispatch(accountActions.setDataFilters(dataFilters));
  };

// Loads the creator's connected Instagram accounts (via team member or owner).
export const loadInitialInstagramAccounts =
  (userId: string): AppThunk =>
  async (dispatch) => {
    const instagramAccountsSnap = await getUserInstagramAccountsSnap(userId);
    const instagramAccounts = instagramAccountsSnap.docs.map((doc) =>
      doc.data()
    );
    dispatch(accountActions.setInstagramAccounts(instagramAccounts));
  };

// Loads the creator's connected Facebook Pages (via team member or owner).
export const loadInitialFacebookPages =
  (userId: string): AppThunk =>
  async (dispatch) => {
    const facebookPagesSnap = await getUserFacebookPagesSnap(userId);
    const facebookPages = facebookPagesSnap.docs.map((doc) => doc.data());
    dispatch(accountActions.setFacebookPages(facebookPages));
  };

// Handles adding, updating, and removing data filters
export const updateDataFilter =
  (
    userId: string,
    dataFilter: DataFilter,
    operation: "add" | "update" | "remove"
  ): AppThunk =>
  async (dispatch, getState) => {
    const { teamMemberAccountData } = getState().account;
    const dataFilterUserId = teamMemberAccountData?.uid || userId;
    const dataFilterRef = getUserDataFilterRef(dataFilterUserId, dataFilter.id);
    if (operation === "add") {
      await setDoc(dataFilterRef, dataFilter);
    } else if (operation === "update") {
      await updateDoc(dataFilterRef, dataFilter);
    } else if (operation === "remove") {
      await deleteDoc(dataFilterRef);
    }
    dispatch(
      accountActions.manageDataFilter({
        operation: operation,
        filter: dataFilter,
      })
    );
  };

// Use getAccountSlice instead of getAccountState, kept this to leave current files untouched instead of replacing all instances
export const getAccountState = (state: AppState) => state;

export const getAccountSlice = (state: AppState) => state.account;
